import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Image as ImageIcon, X, Tag, Eye, EyeOff, Loader2 } from 'lucide-react';
import { photoSrc as resolvePhoto } from '../utils/photo';

const CATEGORIES = [
  { id: 'tours', label_en: 'Tours', label_es: 'Tours' },
  { id: 'yachts', label_en: 'Yachts', label_es: 'Yates' },
  { id: 'nightlife', label_en: 'Nightlife', label_es: 'Vida nocturna' },
  { id: 'activities', label_en: 'Activities', label_es: 'Actividades' },
  { id: 'lodging', label_en: 'Lodging', label_es: 'Hospedaje' },
  { id: 'car_rental', label_en: 'Car rental', label_es: 'Renta de autos' },
  { id: 'other', label_en: 'Other', label_es: 'Otros' },
];

const LOCATIONS = ['playa_del_carmen', 'cancun', 'tulum', 'quintana_roo', 'los_cabos'];

const EMPTY_FORM = {
  title: '',
  description_es: '',
  description_en: '',
  category: 'tours',
  location: 'playa_del_carmen',
  price: '',
  currency: 'USD',
  discount_percent: 0,
  duration: '',
  max_people: '',
  pricing_mode: 'per_person', // 'per_person' or 'flat'
  includes: '',
  includes_en: '',
  photos: [],
  active: true,
};

export default function ProviderProducts() {
  const { api, token } = useAuth();
  const { language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const fetchProducts = async () => {
    try {
      const { data } = await api().get('/provider/products');
      setProducts(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  // Compress to JPEG Blob — 1024px / 0.7 for fast & small uploads
  const compressToBlob = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        let { width, height } = img;
        if (width > MAX) {
          height = Math.round(height * (MAX / width));
          width = MAX;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('blob failed'))),
          'image/jpeg',
          0.7
        );
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const slots = Math.max(0, 5 - form.photos.length);
    const queue = files.slice(0, slots);
    setUploading(true);
    setUploadProgress({ current: 0, total: queue.length });
    try {
      for (let i = 0; i < queue.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await compressToBlob(queue[i]);
        const fd = new FormData();
        fd.append('files', blob, `product-${Date.now()}-${i}.jpg`);
        // eslint-disable-next-line no-await-in-loop
        const resp = await api().post('/upload/product-images', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        const path = resp.data?.paths?.[0];
        if (path) {
          setForm((prev) => ({ ...prev, photos: [...prev.photos, path] }));
        }
        setUploadProgress({ current: i + 1, total: queue.length });
      }
    } catch (err) {
      console.error('product upload error', err);
      toast.error(language === 'en' ? 'Photo upload failed. Try again.' : 'Falló la subida de foto. Reintenta.');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (e.target) e.target.value = '';
    }
  };

  // Resolve photo URL — supports legacy base64 dataURLs + new storage paths
  const photoSrc = (p) => resolvePhoto(p, token);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || '',
      description_es: p.description_es || '',
      description_en: p.description_en || '',
      category: p.category || 'tours',
      location: p.location || 'playa_del_carmen',
      price: p.price || '',
      currency: (p.currency || 'USD').toUpperCase() === 'MXN' ? 'MXN' : 'USD',
      discount_percent: p.discount_percent || 0,
      duration: p.duration || '',
      max_people: p.max_people || '',
      pricing_mode: p.pricing_mode === 'flat' ? 'flat' : 'per_person',
      includes: (p.includes || []).join('\n'),
      includes_en: (p.includes_en || []).join('\n'),
      photos: p.photos || [],
      active: p.active !== false,
    });
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.price || !form.description_es || !form.description_en) {
      toast.error(language === 'en' ? 'Please fill required fields' : 'Completa los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description_es: form.description_es,
        description_en: form.description_en,
        category: form.category,
        location: form.location,
        price: parseFloat(form.price),
        currency: form.currency === 'MXN' ? 'MXN' : 'USD',
        discount_percent: parseFloat(form.discount_percent) || 0,
        duration: form.duration || null,
        max_people: form.max_people ? parseInt(form.max_people) : null,
        pricing_mode: form.pricing_mode === 'flat' ? 'flat' : 'per_person',
        includes: form.includes ? form.includes.split('\n').map(s => s.trim()).filter(Boolean) : [],
        includes_en: form.includes_en ? form.includes_en.split('\n').map(s => s.trim()).filter(Boolean) : [],
        photos: form.photos,
        active: form.active,
      };
      if (editingId) {
        await api().put(`/provider/products/${editingId}`, payload);
        toast.success(language === 'en' ? 'Product updated' : 'Producto actualizado');
      } else {
        await api().post('/provider/products', payload);
        toast.success(language === 'en' ? 'Product created' : 'Producto creado');
      }
      setIsOpen(false);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'en' ? 'Delete this product?' : '¿Eliminar este producto?')) return;
    try {
      await api().delete(`/provider/products/${id}`);
      toast.success(language === 'en' ? 'Deleted' : 'Eliminado');
      fetchProducts();
    } catch (e) {
      toast.error('Error');
    }
  };

  const toggleActive = async (p) => {
    try {
      await api().put(`/provider/products/${p.id}`, { active: !p.active });
      fetchProducts();
    } catch (e) {
      toast.error('Error');
    }
  };

  return (
    <div data-testid="provider-products-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold">
            {language === 'en' ? 'My Catalog' : 'Mi Catálogo'}
          </h2>
          <p className="text-xs text-muted-foreground">
            {language === 'en'
              ? 'Sell experiences directly. Tourists buy and a chat opens with you.'
              : 'Vende experiencias directo. Los turistas compran y se abre un chat contigo.'}
          </p>
        </div>
        <Button data-testid="add-product-btn" onClick={openCreate} className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white">
          <Plus size={16} className="mr-1" /> {language === 'en' ? 'Add' : 'Añadir'}
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-muted-foreground py-6">
          {language === 'en' ? 'Loading...' : 'Cargando...'}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-10 border-2 border-dashed border-border rounded-2xl">
          <ImageIcon className="mx-auto mb-2 opacity-50" />
          <p>{language === 'en' ? 'No products yet. Create your first one!' : 'Aún no tienes productos. ¡Crea el primero!'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`my-product-${p.id}`} className={`bg-card border border-border rounded-2xl overflow-hidden shadow-sm ${!p.active && 'opacity-60'}`}>
              <div className="relative aspect-video">
                <img
                  src={photoSrc(p.photos && p.photos[0]) || 'https://images.unsplash.com/photo-1620694655075-6ed740167a66?auto=format&fit=crop&q=80&w=400'}
                  alt={p.title}
                  className="w-full h-full object-cover"
                />
                {p.discount_percent > 0 && (
                  <span className="absolute top-2 left-2 bg-rosa text-white text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1">
                    <Tag size={10} /> -{p.discount_percent}%
                  </span>
                )}
                <Badge className="absolute top-2 right-2 capitalize bg-black/60 text-white border-0">{p.category}</Badge>
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <p className="font-semibold text-sm line-clamp-1">{p.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{p.description_es}</p>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-heading font-bold text-caribbean">${p.final_price}</span>
                  {p.discount_percent > 0 && (
                    <span className="text-xs text-muted-foreground line-through">${p.price}</span>
                  )}
                  <span className="text-[10px] text-muted-foreground font-semibold">{p.currency || 'USD'}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1 h-8 text-xs">
                    <Edit size={12} className="mr-1" /> {language === 'en' ? 'Edit' : 'Editar'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(p)} className="h-8 w-8 p-0">
                    {p.active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(p.id)} className="h-8 w-8 p-0 text-rosa">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog form */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? (language === 'en' ? 'Edit product' : 'Editar producto')
                : (language === 'en' ? 'New product' : 'Nuevo producto')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{language === 'en' ? 'Title' : 'Título'} *</Label>
              <Input data-testid="product-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{language === 'en' ? 'Category' : 'Categoría'} *</Label>
                <select
                  data-testid="product-category-select"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{language === 'en' ? c.label_en : c.label_es}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{language === 'en' ? 'Location' : 'Ubicación'} *</Label>
                <select
                  data-testid="product-location-select"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm capitalize"
                >
                  {LOCATIONS.map(l => <option key={l} value={l}>{l.replaceAll('_', ' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>{language === 'en' ? 'Price' : 'Precio'} *</Label>
                <Input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <Label>{language === 'en' ? 'Currency' : 'Moneda'}</Label>
                <select
                  data-testid="product-currency-select"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm font-semibold"
                >
                  <option value="USD">USD</option>
                  <option value="MXN">MXN (pesos)</option>
                </select>
              </div>
              <div>
                <Label>{language === 'en' ? 'Discount %' : 'Descuento %'}</Label>
                <Input data-testid="product-discount-input" type="number" min="0" max="90" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{language === 'en' ? 'Duration' : 'Duración'}</Label>
                <Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="3 hours" />
              </div>
              <div>
                <Label>{language === 'en' ? 'Max people' : 'Personas máx'}</Label>
                <Input type="number" value={form.max_people} onChange={(e) => setForm({ ...form, max_people: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>
                {language === 'en' ? 'Pricing mode' : 'Tipo de precio'}
              </Label>
              <select
                data-testid="product-pricing-mode-select"
                value={form.pricing_mode}
                onChange={(e) => setForm({ ...form, pricing_mode: e.target.value })}
                className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm font-semibold"
              >
                <option value="per_person">
                  {language === 'en' ? 'Per person (price × people)' : 'Por persona (precio × personas)'}
                </option>
                <option value="flat">
                  {language === 'en' ? 'Flat / group rate (fixed price)' : 'Grupo / tarifa fija (no se multiplica)'}
                </option>
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">
                {form.pricing_mode === 'flat'
                  ? (language === 'en'
                      ? 'Use this for private tours, yacht rentals or anything sold as a group package.'
                      : 'Úsalo para tours privados, renta de yates o cualquier producto vendido por grupo.')
                  : (language === 'en'
                      ? 'Price will multiply by the number of people booked.'
                      : 'El precio se multiplica por el número de personas.')}
              </p>
            </div>
            <div>
              <Label>{language === 'en' ? 'Description (Spanish)' : 'Descripción (Español)'} *</Label>
              <Textarea data-testid="product-desc-es" rows={3} value={form.description_es} onChange={(e) => setForm({ ...form, description_es: e.target.value })} />
            </div>
            <div>
              <Label>{language === 'en' ? 'Description (English)' : 'Descripción (Inglés)'} *</Label>
              <Textarea data-testid="product-desc-en" rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
            </div>
            <div>
              <Label>{language === 'en' ? 'Includes — Spanish (one per line)' : 'Incluye — Español (uno por línea)'}</Label>
              <Textarea data-testid="product-includes-es" rows={3} value={form.includes} onChange={(e) => setForm({ ...form, includes: e.target.value })} placeholder="Bebidas&#10;Equipo de snorkel" />
            </div>
            <div>
              <Label>{language === 'en' ? 'Includes — English (one per line)' : 'Incluye — Inglés (uno por línea)'}</Label>
              <Textarea data-testid="product-includes-en" rows={3} value={form.includes_en} onChange={(e) => setForm({ ...form, includes_en: e.target.value })} placeholder="Drinks&#10;Snorkel gear" />
            </div>
            <div>
              <Label className="flex items-center justify-between">
                <span>{language === 'en' ? 'Photos (up to 5)' : 'Fotos (hasta 5)'}</span>
                <span className="text-[10px] text-muted-foreground font-normal">{form.photos.length}/5</span>
              </Label>

              {/* Upload progress banner */}
              {uploading && uploadProgress.total > 0 && (
                <div
                  data-testid="product-upload-banner"
                  className="mt-2 rounded-xl bg-caribbean/10 border border-caribbean/30 p-2.5 flex items-center gap-2"
                >
                  <Loader2 className="animate-spin text-caribbean shrink-0" size={16} />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-caribbean">
                      {language === 'en'
                        ? `Uploading photo ${uploadProgress.current}/${uploadProgress.total}…`
                        : `Subiendo foto ${uploadProgress.current}/${uploadProgress.total}…`}
                    </p>
                    <div className="mt-1 h-1 rounded-full bg-caribbean/20 overflow-hidden">
                      <div
                        className="h-full bg-caribbean transition-all duration-300"
                        style={{
                          width: `${Math.round((uploadProgress.current / Math.max(1, uploadProgress.total)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border bg-muted">
                    <img src={photoSrc(p)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      data-testid={`remove-product-photo-${i}`}
                      onClick={() => setForm({ ...form, photos: form.photos.filter((_, idx) => idx !== i) })}
                      className="absolute top-0.5 right-0.5 bg-rosa text-white rounded-full p-0.5"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}

                {/* Pending skeleton tiles */}
                {uploading && Array.from({ length: Math.max(0, uploadProgress.total - uploadProgress.current) }).map((_, i) => (
                  <div
                    key={`prod-skel-${i}`}
                    className="w-20 h-20 rounded-lg border border-border bg-muted flex items-center justify-center animate-pulse"
                  >
                    <Loader2 className="animate-spin text-caribbean" size={16} />
                  </div>
                ))}

                {form.photos.length < 5 && !uploading && (
                  <label
                    data-testid="product-add-photo"
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-muted"
                  >
                    <Plus size={18} />
                    <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                  </label>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {language === 'en'
                  ? 'Photos are compressed and uploaded as you pick them.'
                  : 'Las fotos se comprimen y suben al seleccionarlas.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="product-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="product-active" className="cursor-pointer">
                {language === 'en' ? 'Active (visible to tourists)' : 'Activo (visible para turistas)'}
              </Label>
            </div>
            <Button
              data-testid="product-save-btn"
              onClick={handleSubmit}
              disabled={saving}
              className="w-full bg-caribbean hover:bg-caribbean/90 text-white"
            >
              {saving ? <Loader2 className="animate-spin" /> : (language === 'en' ? 'Save' : 'Guardar')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
